import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Organic lavender/blue blob decorations behind the card — same Midnight &
// Lavender chrome reference as Login.tsx. Purely decorative, non-interactive.
const RegistrationBlobs: React.FC = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
    <div className="absolute -top-24 -left-24 h-96 w-96 rounded-[45%_55%_60%_40%/50%_45%_55%_50%] bg-accent opacity-20 blur-3xl" />
    <div className="absolute top-1/3 -right-32 h-[28rem] w-[28rem] rounded-[55%_45%_50%_50%/40%_55%_45%_60%] bg-[#5A9CFF] opacity-20 blur-3xl" />
    <div className="absolute -bottom-32 left-1/4 h-80 w-80 rounded-[50%_50%_45%_55%/55%_45%_55%_45%] bg-accent opacity-10 blur-3xl" />
  </div>
);

const Registration: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);

  const verifyToken = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/verify-registration-token`, {
        token
      });

      if (response.data.valid) {
        setValid(true);
        setUserName(response.data.user.name);
        setUserEmail(response.data.user.email);
        setExpiresAt(new Date(response.data.expiresAt));
      } else {
        setError(response.data.message || 'Invalid or expired registration link');
      }
    } catch (err: any) {
      console.error('Token verification error:', err);
      setError(err.response?.data?.message || 'Invalid or expired registration link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    verifyToken();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/complete-registration`, {
        token,
        password
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        // Store access token
        localStorage.setItem('accessToken', response.data.accessToken);

        // Redirect to home
        window.location.href = '/';
      }
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <RegistrationBlobs />
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-active"></div>
          </div>
          <p className="mt-4 text-center text-sm text-ink-muted">Verifying registration link...</p>
        </div>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="relative min-h-screen overflow-hidden bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <RegistrationBlobs />
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src="/eskimi-logo-medium.png"
              alt="Eskimi Logo"
              className="h-12 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-ink">
            Invalid Registration Link
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="rounded-md bg-risk-bg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-risk" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-risk">
                    {error}
                  </h3>
                  <div className="mt-2 text-sm text-risk">
                    <p>
                      This registration link may have expired or already been used. Please contact your administrator for a new registration link.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={() => navigate('/login')}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-active focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
              >
                Go to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <RegistrationBlobs />
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/eskimi-logo-medium.png"
            alt="Eskimi Logo"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-ink">
          Set Your Password
        </h2>
        <p className="mt-2 text-center text-sm text-ink-muted">
          Create or reset your password to access Eskimi DeepContext
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {/* User Info */}
          <div className="mb-6 p-4 bg-nav-active border border-line rounded-md">
            <p className="text-sm font-medium text-ink">{userName}</p>
            <p className="text-sm text-ink-muted">{userEmail}</p>
            {expiresAt && (
              <p className="text-xs text-ink-muted mt-2">
                Link expires: {expiresAt.toLocaleString()}
              </p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-md bg-risk-bg p-4">
                <div className="text-sm text-risk">{error}</div>
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ink">
                Password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-line rounded-md shadow-sm placeholder-ink-muted focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                />
              </div>
              <p className="mt-1 text-xs text-ink-muted">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-ink">
                Confirm Password
              </label>
              <div className="mt-1">
                <input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 border border-line rounded-md shadow-sm placeholder-ink-muted focus:outline-none focus:ring-accent focus:border-accent sm:text-sm"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-active focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-3"></div>
                    Setting password...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-line" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-surface text-ink-muted">
                  Security
                </span>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="flex items-center text-xs text-ink-muted">
                <svg className="h-4 w-4 text-safe mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Passwords are securely encrypted
              </div>
              <div className="flex items-center text-xs text-ink-muted">
                <svg className="h-4 w-4 text-safe mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Secure one-time link expires in 24 hours
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
